import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { SETTINGS } from '../configs/common.settings';

export interface PermissionCatalogItem {
  code: string;
  name: string;
  label: string;
  module: string;
  description?: string;
}

export interface PermissionTemplate {
  _id?: string;
  name: string;
  description?: string;
  actions: string[];
  isSystem?: boolean;
  isStandard?: boolean;
  role?: string | null;
  usage?: { roles: number; users: number };
}

export interface PermissionAssignment {
  _id?: string;
  scope: 'role' | 'company' | 'department';
  role?: string | null;
  companyId?: string | null;
  department?: string | null;
  templateId: any;
}

export interface EffectivePermissions {
  userId: string;
  companyId?: string | null;
  role: string;
  department?: string | null;
  actions: string[];
  templateId?: string | null;
  baseline?: { role: string[]; user: string[] | null };
  override?: { grants: string[]; denies: string[] };
}

export interface UserPermissionOverride {
  userId: string;
  companyId?: string | null;
  templateId?: any;
  grants: string[];
  denies: string[];
}

@Injectable({
  providedIn: 'root',
})
export class PermissionAccessService {
  private base = `${SETTINGS.BASE_API}/revamp`;

  constructor(private http: HttpClient) {}

  seed(): Observable<any> {
    return this.http.post(`${this.base}/permissions/seed`, {});
  }

  catalog(): Observable<{ items: PermissionCatalogItem[] }> {
    return this.http.get<{ items: PermissionCatalogItem[] }>(
      `${this.base}/permissions/catalog`
    );
  }

  templates(): Observable<{ items: PermissionTemplate[] }> {
    return this.http.get<{ items: PermissionTemplate[] }>(
      `${this.base}/permission-templates`
    );
  }

  createTemplate(body: Partial<PermissionTemplate>): Observable<PermissionTemplate> {
    return this.http.post<PermissionTemplate>(
      `${this.base}/permission-templates`,
      body
    );
  }

  updateTemplate(body: Partial<PermissionTemplate> & { _id: string }): Observable<PermissionTemplate> {
    return this.http.put<PermissionTemplate>(
      `${this.base}/permission-templates`,
      body
    );
  }

  deleteTemplate(id: string): Observable<any> {
    return this.http.delete(`${this.base}/permission-templates/${id}`);
  }

  resetTemplate(id: string): Observable<PermissionTemplate> {
    return this.http.post<PermissionTemplate>(
      `${this.base}/permission-templates/${id}/reset`,
      {}
    );
  }

  assignments(params?: {
    scope?: string;
    companyId?: string;
  }): Observable<{ items: PermissionAssignment[] }> {
    const q = new URLSearchParams();
    if (params?.scope) q.set('scope', params.scope);
    if (params?.companyId) q.set('companyId', params.companyId);
    const qs = q.toString();
    return this.http.get<{ items: PermissionAssignment[] }>(
      `${this.base}/permission-assignments${qs ? `?${qs}` : ''}`
    );
  }

  upsertAssignment(body: Partial<PermissionAssignment>): Observable<PermissionAssignment> {
    return this.http.put<PermissionAssignment>(
      `${this.base}/permission-assignments`,
      body
    );
  }

  effective(params: {
    userId?: string;
    companyId?: string;
    role?: string;
    department?: string;
  }): Observable<EffectivePermissions> {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') q.set(k, String(v));
    });
    return this.http.get<EffectivePermissions>(
      `${this.base}/permissions/effective?${q.toString()}`
    );
  }

  getUserOverride(
    userId: string,
    companyId?: string
  ): Observable<UserPermissionOverride> {
    const qs = companyId ? `?companyId=${companyId}` : '';
    return this.http.get<UserPermissionOverride>(
      `${this.base}/permissions/user/${userId}${qs}`
    );
  }

  saveUserOverride(
    userId: string,
    body: {
      companyId?: string | null;
      templateId?: string | null;
      grants?: string[];
      denies?: string[];
      consolidate?: boolean;
    }
  ): Observable<UserPermissionOverride> {
    return this.http.put<UserPermissionOverride>(
      `${this.base}/permissions/user/${userId}`,
      body
    );
  }

  departments(companyId: string): Observable<{ items: string[] }> {
    return this.http.get<{ items: string[] }>(
      `${this.base}/permissions/departments/${companyId}`
    );
  }
}
